import React, { useEffect, useState } from "react";
import axios from "axios";
import API_CONFIG from "../components/API_CONFIG/API_CONFIG";
import { 
  FiUser, 
  FiPhone, 
  FiMail, 
  FiBriefcase, 
  FiCheckCircle, 
  FiCalendar,
  FiClock,
  FiGlobe,
  FiMapPin,
  FiAward,
  FiShield,
  FiEdit2,
  FiMoreVertical,
  FiBell,
  FiSettings,
  FiLogOut
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const UserProfile = ({ userId }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState('profile');
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        setError("");
        const res = await axios.get(
          `${API_CONFIG.apiUrl}profilUser.php?userId=${userId}`
        );
        if (res.data.success) {
          setUser(res.data.data);
        } else {
          setError(res.data.message || "Erreur de chargement");
        }
      } catch (err) {
        setError("Erreur serveur ou réseau");
      } finally {
        setLoading(false);
      }
    };

    if (userId) fetchUser();
  }, [userId]);

  // Statistiques fictives pour enrichir le profil
  const stats = [
    { label: "Projets", value: "12", icon: FiBriefcase, color: "#3b82f6" },
    { label: "Tâches", value: "48", icon: FiCheckCircle, color: "#10b981" },
    { label: "Réunions", value: "24", icon: FiCalendar, color: "#8b5cf6" },
    { label: "Heures", value: "156", icon: FiClock, color: "#f59e0b" }
  ];

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    .profile-wrapper {
      min-height: 100vh;
      background: linear-gradient(135deg, #0a0e27 0%, #1a1a3e 50%, #0a0e27 100%);
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 20px;
      font-family: 'Inter', system-ui, -apple-system, sans-serif;
      position: relative;
      overflow: hidden;
    }

    /* Particules animées */
    .particles {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      overflow: hidden;
      z-index: 0;
    }

    .particle {
      position: absolute;
      width: 4px;
      height: 4px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 50%;
      animation: floatParticle 15s infinite linear;
    }

    @keyframes floatParticle {
      0% {
        transform: translateY(100vh) rotate(0deg);
        opacity: 0;
      }
      10% {
        opacity: 1;
      }
      90% {
        opacity: 1;
      }
      100% {
        transform: translateY(-100vh) rotate(720deg);
        opacity: 0;
      }
    }

    /* Blobs animés */
    .bg-blob {
      position: absolute;
      border-radius: 50%;
      filter: blur(80px);
      opacity: 0.3;
      z-index: 0;
      animation: blobFloat 20s ease-in-out infinite;
    }

    .blob-1 {
      width: 500px;
      height: 500px;
      background: linear-gradient(135deg, #1e40af, #3b82f6);
      top: -200px;
      left: -200px;
    }

    .blob-2 {
      width: 400px;
      height: 400px;
      background: linear-gradient(135deg, #7c3aed, #6366f1);
      bottom: -150px;
      right: -150px;
      animation-delay: -5s;
    }

    .blob-3 {
      width: 300px;
      height: 300px;
      background: linear-gradient(135deg, #059669, #10b981);
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      animation-delay: -10s;
      opacity: 0.1;
    }

    @keyframes blobFloat {
      0%, 100% { transform: translate(0, 0) scale(1); }
      25% { transform: translate(30px, -40px) scale(1.1); }
      50% { transform: translate(-20px, 20px) scale(0.9); }
      75% { transform: translate(40px, 30px) scale(1.05); }
    }

    /* Carte principale */
    .profile-card {
      width: 100%;
      max-width: 1100px;
      background: rgba(15, 23, 42, 0.7);
      backdrop-filter: blur(20px) saturate(180%);
      -webkit-backdrop-filter: blur(20px) saturate(180%);
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 40px;
      padding: 0;
      position: relative;
      z-index: 1;
      box-shadow: 
        0 30px 60px -20px rgba(0, 0, 0, 0.8),
        0 0 40px rgba(59, 130, 246, 0.05),
        inset 0 1px 0 rgba(255, 255, 255, 0.05);
      overflow: hidden;
      animation: cardAppear 0.8s cubic-bezier(0.16, 1, 0.3, 1);
    }

    @keyframes cardAppear {
      from {
        opacity: 0;
        transform: scale(0.95) translateY(20px);
      }
      to {
        opacity: 1;
        transform: scale(1) translateY(0);
      }
    }

    /* Bandeau supérieur avec dégradé */
    .card-header-gradient {
      height: 120px;
      background: linear-gradient(135deg, 
        rgba(59, 130, 246, 0.3) 0%, 
        rgba(139, 92, 246, 0.3) 50%, 
        rgba(16, 185, 129, 0.3) 100%
      );
      position: relative;
      overflow: hidden;
    }

    .card-header-gradient::after {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%);
      animation: shimmer 10s infinite linear;
    }

    @keyframes shimmer {
      0% { transform: rotate(0deg) translateY(-30%); }
      100% { transform: rotate(360deg) translateY(30%); }
    }

    /* En-tête avec avatar */
    .profile-header {
      display: flex;
      align-items: flex-end;
      padding: 0 40px 30px;
      margin-top: -60px;
      position: relative;
      gap: 30px;
      flex-wrap: wrap;
    }

    .avatar-wrapper {
      position: relative;
      flex-shrink: 0;
    }

    .avatar-container {
      width: 120px;
      height: 120px;
      border-radius: 50%;
      background: linear-gradient(135deg, #1a1a3e, #2d2d6e);
      border: 4px solid rgba(255, 255, 255, 0.1);
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 48px;
      font-weight: 700;
      color: white;
      position: relative;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
      transition: all 0.3s ease;
      cursor: pointer;
    }

    .avatar-container:hover {
      transform: scale(1.05);
      border-color: rgba(59, 130, 246, 0.5);
      box-shadow: 0 15px 40px rgba(59, 130, 246, 0.2);
    }

    .avatar-status {
      position: absolute;
      bottom: 8px;
      right: 8px;
      width: 20px;
      height: 20px;
      background: #10b981;
      border-radius: 50%;
      border: 3px solid #0a0e27;
      animation: pulse 2s infinite;
    }

    @keyframes pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.2); }
    }

    .user-info {
      flex: 1;
      min-width: 200px;
    }

    .user-name {
      font-size: 2rem;
      font-weight: 700;
      color: #f1f5f9;
      margin-bottom: 4px;
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
    }

    .user-role {
      font-size: 0.9rem;
      color: #94a3b8;
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
    }

    .role-badge {
      background: rgba(59, 130, 246, 0.2);
      color: #60a5fa;
      padding: 4px 14px;
      border-radius: 20px;
      font-size: 0.75rem;
      font-weight: 600;
      border: 1px solid rgba(59, 130, 246, 0.2);
    }

    .user-id {
      font-size: 0.8rem;
      color: #475569;
      font-family: 'Courier New', monospace;
    }

    /* Actions rapides */
    .quick-actions {
      display: flex;
      gap: 10px;
      margin-left: auto;
      align-items: center;
    }

    .action-btn {
      width: 40px;
      height: 40px;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.08);
      color: #94a3b8;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.3s ease;
      font-size: 18px;
    }

    .action-btn:hover {
      background: rgba(59, 130, 246, 0.15);
      border-color: rgba(59, 130, 246, 0.3);
      color: #60a5fa;
      transform: translateY(-2px);
    }

    /* Stats */
    .stats-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 16px;
      padding: 0 40px 30px;
    }

    .stat-item {
      background: rgba(255, 255, 255, 0.03);
      border: 1px solid rgba(255, 255, 255, 0.05);
      border-radius: 16px;
      padding: 16px 20px;
      display: flex;
      align-items: center;
      gap: 16px;
      transition: all 0.3s ease;
      cursor: pointer;
    }

    .stat-item:hover {
      background: rgba(255, 255, 255, 0.06);
      transform: translateY(-3px);
      border-color: rgba(59, 130, 246, 0.2);
    }

    .stat-icon-wrapper {
      width: 44px;
      height: 44px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      flex-shrink: 0;
    }

    .stat-content {
      flex: 1;
    }

    .stat-value {
      font-size: 1.3rem;
      font-weight: 700;
      color: #f1f5f9;
      line-height: 1;
    }

    .stat-label {
      font-size: 0.75rem;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    /* Contenu principal */
    .profile-content {
      padding: 0 40px 40px;
    }

    /* Tabs */
    .tabs-container {
      display: flex;
      gap: 4px;
      background: rgba(255, 255, 255, 0.03);
      border-radius: 14px;
      padding: 4px;
      margin-bottom: 30px;
      border: 1px solid rgba(255, 255, 255, 0.05);
    }

    .tab-btn {
      flex: 1;
      padding: 12px 20px;
      border: none;
      background: transparent;
      color: #64748b;
      font-family: 'Inter', sans-serif;
      font-size: 0.9rem;
      font-weight: 500;
      border-radius: 10px;
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }

    .tab-btn:hover {
      color: #e2e8f0;
      background: rgba(255, 255, 255, 0.05);
    }

    .tab-btn.active {
      background: rgba(59, 130, 246, 0.15);
      color: #60a5fa;
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
    }

    /* Grille d'informations */
    .info-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 16px;
    }

    .info-card {
      background: rgba(255, 255, 255, 0.03);
      border: 1px solid rgba(255, 255, 255, 0.05);
      border-radius: 16px;
      padding: 20px 24px;
      transition: all 0.3s ease;
      position: relative;
      overflow: hidden;
    }

    .info-card::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.3), transparent);
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    .info-card:hover::before {
      opacity: 1;
    }

    .info-card:hover {
      background: rgba(255, 255, 255, 0.06);
      transform: translateY(-2px);
      border-color: rgba(59, 130, 246, 0.15);
    }

    .info-label {
      display: flex;
      align-items: center;
      gap: 10px;
      color: #64748b;
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-bottom: 8px;
      font-weight: 600;
    }

    .info-label svg {
      font-size: 16px;
    }

    .info-value {
      color: #f1f5f9;
      font-size: 1.05rem;
      font-weight: 500;
      padding-left: 26px;
    }

    .info-value.status-active {
      color: #4ade80;
    }

    /* Badge de statut */
    .status-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 14px;
      border-radius: 20px;
      font-size: 0.8rem;
      font-weight: 500;
    }

    .status-badge.active {
      background: rgba(16, 185, 129, 0.15);
      color: #4ade80;
      border: 1px solid rgba(16, 185, 129, 0.2);
    }

    /* Loader amélioré */
    .loader-wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 20px;
    }

    .loader-spinner {
      width: 60px;
      height: 60px;
      border: 3px solid rgba(59, 130, 246, 0.1);
      border-radius: 50%;
      border-top-color: #3b82f6;
      animation: spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
    }

    .loader-text {
      color: #64748b;
      font-size: 0.9rem;
      animation: pulse 2s infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    /* Responsive */
    @media (max-width: 1024px) {
      .stats-grid {
        grid-template-columns: repeat(2, 1fr);
      }
    }

    @media (max-width: 768px) {
      .profile-card {
        border-radius: 24px;
      }

      .profile-header {
        flex-direction: column;
        align-items: center;
        text-align: center;
        padding: 0 20px 20px;
        gap: 16px;
      }

      .user-info {
        text-align: center;
      }

      .user-name {
        justify-content: center;
        font-size: 1.5rem;
      }

      .user-role {
        justify-content: center;
      }

      .quick-actions {
        margin-left: 0;
        justify-content: center;
        width: 100%;
      }

      .stats-grid {
        grid-template-columns: repeat(2, 1fr);
        padding: 0 20px 20px;
        gap: 10px;
      }

      .profile-content {
        padding: 0 20px 30px;
      }

      .info-grid {
        grid-template-columns: 1fr;
        gap: 12px;
      }

      .tabs-container {
        overflow-x: auto;
        flex-wrap: nowrap;
        padding: 4px;
        gap: 4px;
      }

      .tab-btn {
        flex: 0 0 auto;
        padding: 10px 16px;
        font-size: 0.8rem;
        white-space: nowrap;
      }

      .avatar-container {
        width: 90px;
        height: 90px;
        font-size: 36px;
      }

      .card-header-gradient {
        height: 80px;
      }

      .stat-item {
        padding: 12px 16px;
      }

      .stat-icon-wrapper {
        width: 36px;
        height: 36px;
        font-size: 16px;
      }

      .stat-value {
        font-size: 1rem;
      }
    }

    @media (max-width: 480px) {
      .stats-grid {
        grid-template-columns: 1fr 1fr;
        gap: 8px;
      }

      .stat-item {
        padding: 10px 14px;
      }

      .info-card {
        padding: 16px 18px;
      }

      .info-value {
        font-size: 0.95rem;
        padding-left: 0;
      }
    }
  `;

  if (loading) {
    return (
      <div className="profile-wrapper">
        <style>{styles}</style>
        <div className="particles">
          {[...Array(20)].map((_, i) => (
            <div 
              key={i}
              className="particle"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 15}s`,
                animationDuration: `${15 + Math.random() * 10}s`,
                width: `${2 + Math.random() * 4}px`,
                height: `${2 + Math.random() * 4}px`
              }}
            />
          ))}
        </div>
        <div className="loader-wrapper">
          <div className="loader-spinner" />
          <div className="loader-text">Chargement du profil...</div>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="profile-wrapper">
        <style>{styles}</style>
        <div className="bg-blob blob-1" />
        <div className="bg-blob blob-2" />
        <div className="profile-card" style={{ padding: '40px', textAlign: 'center' }}>
          <div style={{ color: '#fca5a5', fontSize: '1.2rem' }}>
            {error || "Utilisateur introuvable"}
          </div>
        </div>
      </div>
    );
  }

  // Générer les initiales
  const initials = `${user.nom?.charAt(0) || ''}${user.prenom?.charAt(0) || ''}`;

  return (
    <div className="profile-wrapper">
      <style>{styles}</style>

      {/* Particules */}
      <div className="particles">
        {[...Array(30)].map((_, i) => (
          <div 
            key={i}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 15}s`,
              animationDuration: `${15 + Math.random() * 15}s`,
              width: `${2 + Math.random() * 4}px`,
              height: `${2 + Math.random() * 4}px`,
              opacity: 0.1 + Math.random() * 0.2
            }}
          />
        ))}
      </div>

      {/* Blobs */}
      <div className="bg-blob blob-1" />
      <div className="bg-blob blob-2" />
      <div className="bg-blob blob-3" />

      {/* Carte principale */}
      <motion.div 
        className="profile-card"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Bandeau gradient */}
        <div className="card-header-gradient" />

        {/* En-tête */}
        <div className="profile-header">
          <div className="avatar-wrapper">
            <div className="avatar-container">
              {initials || '👤'}
              <div className="avatar-status" />
            </div>
          </div>

          <div className="user-info">
            <div className="user-name">
              {user.prenom} {user.nom}
              <span className="role-badge">{user.fonction || 'Utilisateur'}</span>
            </div>
            <div className="user-role">
              <span>ID: <span className="user-id">#{userId}</span></span>
              <span>•</span>
              <span>Membre depuis {user.created || 'N/A'}</span>
            </div>
          </div>

          <div className="quick-actions">
            <button className="action-btn" title="Notifications">
              <FiBell />
            </button>
            <button className="action-btn" title="Modifier le profil">
              <FiEdit2 />
            </button>
            <button className="action-btn" title="Paramètres">
              <FiSettings />
            </button>
            <button className="action-btn" style={{ color: '#ef4444' }} title="Déconnexion">
              <FiLogOut />
            </button>
          </div>
        </div>

        {/* Statistiques */}
        <div className="stats-grid">
          {stats.map((stat, index) => (
            <motion.div 
              key={index}
              className="stat-item"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ scale: 1.02 }}
            >
              <div 
                className="stat-icon-wrapper"
                style={{ 
                  background: `${stat.color}20`,
                  color: stat.color
                }}
              >
                <stat.icon />
              </div>
              <div className="stat-content">
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Contenu */}
        <div className="profile-content">
          {/* Tabs */}
          <div className="tabs-container">
            {[
              { id: 'profile', label: 'Profil', icon: FiUser },
              { id: 'activity', label: 'Activité', icon: FiClock },
              { id: 'security', label: 'Sécurité', icon: FiShield }
            ].map(tab => (
              <button
                key={tab.id}
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon />
                {tab.label}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <div className="info-grid">
                <div className="info-card">
                  <div className="info-label">
                    <FiUser style={{ color: '#3b82f6' }} />
                    Nom complet
                  </div>
                  <div className="info-value">{user.nom} {user.prenom}</div>
                </div>

                <div className="info-card">
                  <div className="info-label">
                    <FiMail style={{ color: '#8b5cf6' }} />
                    Email
                  </div>
                  <div className="info-value">{user.email || '—'}</div>
                </div>

                <div className="info-card">
                  <div className="info-label">
                    <FiPhone style={{ color: '#10b981' }} />
                    Téléphone
                  </div>
                  <div className="info-value">{user.tel || '—'}</div>
                </div>

                <div className="info-card">
                  <div className="info-label">
                    <FiBriefcase style={{ color: '#f59e0b' }} />
                    Fonction
                  </div>
                  <div className="info-value">{user.fonction || 'Non spécifié'}</div>
                </div>

                <div className="info-card">
                  <div className="info-label">
                    <FiCheckCircle style={{ color: '#4ade80' }} />
                    Statut
                  </div>
                  <div className="info-value">
                    <span className="status-badge active">
                      <FiCheckCircle size={14} />
                      Actif
                    </span>
                  </div>
                </div>

                <div className="info-card">
                  <div className="info-label">
                    <FiCalendar style={{ color: '#ec4899' }} />
                    Date de naissance
                  </div>
                  <div className="info-value">{user.dn || '—'}</div>
                </div>

                <div className="info-card" style={{ gridColumn: '1 / -1' }}>
                  <div className="info-label">
                    <FiClock style={{ color: '#6366f1' }} />
                    Informations d'inscription
                  </div>
                  <div className="info-value" style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                    <span>📅 {user.created || 'N/A'}</span>
                    <span>⏰ {user.heure || 'N/A'}</span>
                    <span>🆔 #{userId}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default UserProfile;